import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../../../../theme/style.scss";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Popover,
  Pagination,
  Stack,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
} from "@mui/material";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import CustomBreadcrumb from "../../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import { AlertType } from "../../../../services/models/AlertType";
import SkeletonWrapper from "../../../../components/Skeleton/SkeletonWrapper";
import { EncryptDecryptService } from "../../../../services/common/EncryptionService";
import { PAGE_SIZE } from "../../../../services/common/const";
import { PreferencesContext } from "../../../../PreferenceContext";
import {
  DeleteVitreous,
  GetVitreousList,
} from "../../../../services/Ophthal/OphthalPosteriorSegmentFundusService";

interface Data {
  opthalPosteriorSegmentFundusMasterId: number;
  code: string;
  name: string;
}

type Order = "asc" | "desc" | "";

interface EnhancedTableProps {
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}

interface Parameter {
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

const parameters: Parameter = {
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
};

interface ResultData {
  opthalPosteriorSegmentFundusMasterColl: Array<any>;
  totalCount: number;
}

const initResultData: ResultData = {
  opthalPosteriorSegmentFundusMasterColl: [],
  totalCount: 0,
};

const skeletonRowCount = 5;

export default function OphthalPosteriorSegmentFundusVitreousList() {
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);

  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  let urlParams = useQuery();
  let page = urlParams.get("page");
  let encodedQuery = urlParams.get("q");
  let query = "";
  let curPage = 1;
  if (
    page != null &&
    page != "" &&
    page != undefined &&
    encodedQuery != null &&
    encodedQuery != undefined
  ) {
    curPage = +page;
    encodedQuery = encodeURIComponent(encodedQuery);
    query = decodeURIComponent(encodedQuery);
  } else {
    encodedQuery = encodeURIComponent("");
    query = "";
    curPage = 1;
  }

  const [param] = useState(parameters);
  const [order] = React.useState<Order>("");
  const [orderBy] = React.useState<keyof Data>("name");
  const [value, setValue] = useState(query || "");
  const [counData, setCounData] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageStartIndex, setPageStartIndex] = useState(1);
  const [pageEndIndex, setPageEndIndex] = useState(1);
  const [openDeleteDilog, setOpenDeleteDilog] = React.useState(false);
  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [masterId, setMasterId] = useState(0);
  const [anchorEl, setAnchorEl3] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenMore = (
    event: React.MouseEvent<HTMLButtonElement>,
    id: any,
  ) => {
    setAnchorEl3(event.currentTarget);
    setMasterId(id);
  };

  const handleCloseMore = () => {
    setAnchorEl3(null);
  };

  function EnhancedTableHead(_props: EnhancedTableProps) {
    return (
      <TableHead className="table-header-color">
        <TableRow>
          <TableCell
            sx={{
              display: { sm: "table-cell", md: "table-cell", lg: "table-cell" },
              fontWeight: "bold",
              width: "50%",
            }}
            align="left"
          >
            Name
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "5%",
            }}
            align="left"
          ></TableCell>
        </TableRow>
      </TableHead>
    );
  }

  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number,
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number],
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const getMasterList = async () => {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("departmentId", userContext.departmentId?.toString());
      params.append("specialityId", userContext.specialityId?.toString());
      params.append("pageIndex", curPage?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", query || "");
      params.append("mode", "");
      const result: any = await GetVitreousList(params);
      if (result?.data?.data?.opthalPosteriorSegmentFundusMasterColl) {
        const resultData: ResultData = result?.data?.data;
        let masterColl = resultData?.opthalPosteriorSegmentFundusMasterColl || [];
        let dataCount = resultData?.totalCount || 0;
        let showDataCount = Math.ceil(dataCount / param.pageSize);
        setCounData(showDataCount);
        setResultData(resultData);
        let startIndex = (curPage - 1) * param.pageSize + 1;
        let endIndex =
          (curPage - 1) * param.pageSize + (masterColl?.length || 0);
        setPageStartIndex(startIndex);
        setPageEndIndex(endIndex);
        setTotalCount(dataCount);
      } else {
        setResultData(initResultData);
        setCounData(0);
        setTotalCount(0);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  const handleChangePageNew = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    if (curPage !== value) {
      navigate("?page=" + value + "&q=" + encodedQuery);
    }
  };

  function searchValue(val: any) {
    val = val.trim();
    val = encodeURIComponent(val);
    navigate("?page=1&q=" + val);
  }

  const handleUpdate = (id: any) => {
    let encryptId: any = edService.encrypt(id);
    navigate(
      {
        pathname: "vitreous-save",
        search: `?q=${encodeURIComponent(encryptId)}`,
      },
      {
        state: {
          title: " Edit",
          button: "Update",
          OphthalPosteriorSegmentFundusVitreousList: id,
        },
      },
    );
  };

  const handleDeleteDilog = (value: any) => {
    setOpenDeleteDilog(value);
  };


async function deleteRow(ids: any) {
  if (isDeleting) return;

  try {
    setIsDeleting(true);
    setAnchorEl3(null);
    handleDeleteDilog(false);

    const DeleteData = {
      opthalPosteriorSegmentFundusMasterIds: ids?.toString(),
      consumerId: userContext?.consumerId?.toString(),
      orgId: userContext?.organizationId?.toString(),
      branchId: userContext?.branchId?.toString(),
      departmentId: userContext?.departmentId?.toString(),
      specialityId: userContext?.specialityId?.toString(),
      isDeleted: true,
      dataManagedBy: userContext?.loginDetail?.userId,
    };

    const result: any = await DeleteVitreous(DeleteData);

    if (result) {
      await getMasterList();

      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: result?.data?.message || result?.message || "Vitreous record deleted successfully.",
        alertType: AlertType.Success,
      });
    }
  } catch (e: any) {
    updateUserContext({
      ...userContext,
      isAlert: true,
      alertMessage:
        typeof e === "string"
          ? e
          : e?.message || "Failed to delete Vitreous record.",
      alertType: AlertType.Error,
    });
  } finally {
    setIsDeleting(false);
  }
}


  const handleCheckSelectAll = (_event: React.ChangeEvent<HTMLInputElement>) => {
    // reserved for future multi-select
  };

  useEffect(() => {
    setIsLoading(true);
    getMasterList();
  }, [param, curPage, query]);

  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50);
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <>
      <Box>
        <Box className="main-container">
          <Grid container className="page-header">
            <Grid item xs={6}>
              <Box
                sx={{ display: "flex", height: "100%", alignItems: "center" }}
              >
                <CustomBreadcrumb />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    handleUpdate(0);
                  }}
                  size="medium"
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid container className="list-container">
            <Grid item xs={8} sm={8} md={8} lg={10} sx={{ gap: 2 }}>
              <SkeletonWrapper
                isLoading={showSkeleton}
                className="skeleton-search-box"
              >
                <TextField
                  id="searchBox"
                  placeholder="Search..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      searchValue(value);
                    }
                  }}
                  type="text"
                  variant="outlined"
                  size="small"
                  onChange={(e) => setValue(e.target.value)}
                  value={value}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          size="small"
                          onClick={() => {
                            searchValue(value);
                          }}
                        >
                          {" "}
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                    endAdornment: value && (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => {
                          searchValue("");
                          setValue("");
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    ),
                  }}
                />
              </SkeletonWrapper>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} mt={2}>
              <TableContainer>
                <Table>
                  <EnhancedTableHead
                    numSelected={
                      resultData?.opthalPosteriorSegmentFundusMasterColl?.length || 0
                    }
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleCheckSelectAll}
                    rowCount={resultData?.opthalPosteriorSegmentFundusMasterColl?.length}
                  />
                  <TableBody>
                    {showSkeleton ? (
                      Array(skeletonRowCount)
                        ?.fill(0)
                        ?.map((_skeletonRow, index) => {
                          return (
                            <TableRow key={"skeleton-row" + index?.toString()}>
                              <TableCell colSpan={8}>
                                <SkeletonWrapper
                                  isLoading={showSkeleton}
                                  className="skeleton-input"
                                >
                                  <span></span>
                                </SkeletonWrapper>
                              </TableCell>
                            </TableRow>
                          );
                        })
                    ) : (
                      <>
                        {stableSort(
                          resultData?.opthalPosteriorSegmentFundusMasterColl,
                          getComparator(order, orderBy),
                        ).map((item: any) => {
                          return (
                            <TableRow
                              key={item?.opthalPosteriorSegmentFundusMasterId?.toString()}
                            >
                              <TableCell>
                                {!item.isLocked ? (
                                  <Link
                                    className="text-link"
                                    to={{
                                      pathname: "vitreous-save",
                                      search: `?q=${encodeURIComponent(
                                        edService.encrypt(
                                          item?.opthalPosteriorSegmentFundusMasterId|| 0,
                                        ) || 0,
                                      )}`,
                                    }}
                                  >
                                    {item.code ? `${item.code} - ${item.name}` : item.name}
                                  </Link>
                                ) : (
                                  <Typography sx={{ fontWeight: 500 }}>
                                     {item.code ? `${item.code} - ${item.name}` : item.name}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="More" placement="top">
                                  <IconButton
                                    onClick={(e: any) => {
                                      handleOpenMore(
                                        e,
                                        item?.opthalPosteriorSegmentFundusMasterId || 0,
                                      );
                                    }}
                                  >
                                    <MoreVertRoundedIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {resultData?.opthalPosteriorSegmentFundusMasterColl?.length <=
                          0 && (
                          <TableRow>
                            <TableCell align="center" colSpan={8}>
                              No record(s) found
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} paddingTop={1}>
              {resultData?.opthalPosteriorSegmentFundusMasterColl?.length > 0 && (
                <Box className="pagination">
                  <Box className="pagination-label">
                    <SkeletonWrapper
                      isLoading={showSkeleton}
                      className="skeleton-pagination-label"
                    >
                      {pageStartIndex} - {pageEndIndex} of {totalCount}
                    </SkeletonWrapper>
                  </Box>
                  <Box className="pagination-stack">
                    <SkeletonWrapper
                      isLoading={showSkeleton}
                      className="skeleton-pagination-stack"
                    >
                      <Stack component="div">
                        <Pagination
                          color="primary"
                          count={counData}
                          page={curPage}
                          onChange={handleChangePageNew}
                        />
                      </Stack>
                    </SkeletonWrapper>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Popover
        elevation={1}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMore}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                handleUpdate(masterId);
              }}
            >
              <ListItemText primary="Edit" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              className="delete-container"
              onClick={() => {
                handleDeleteDilog(true);
              }}
            >
              Delete
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>

      <Dialog
        open={openDeleteDilog}
        onClose={() => {
          handleDeleteDilog(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this Vitreous record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              deleteRow(masterId);
            }}
            className="button-delete"
            disabled={isDeleting}
          >
            Yes
          </Button>
          <Button
            onClick={() => {
              handleDeleteDilog(false);
            }}
            autoFocus
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
